// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { ActionIcon, Box, Sx, Text, Tooltip, useMantineTheme } from '@mantine/core';
import { useElementSize } from '@mantine/hooks';
import { FC, Key, MouseEvent, ReactNode } from 'react';
import { IconFilter } from '@tabler/icons';

const BookingStateItem: FC<{
  label: string;
  active: boolean;
  color?: string;
  textColor?: string;
  bgColor?: string;
  onClick?: () => void;
  icon?: ReactNode;
  key?: Key;
  textStyle?: Sx;
  sx?: Sx;
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
}> = ({
  label,
  active,
  onClick,
  bgColor = 'transparent',
  color,
  textColor,
  icon,
  key,
  sx,
  textStyle,
  secondaryAction,
}) => {
  const { colorScheme, colors } = useMantineTheme();
  const { ref, width } = useElementSize();
  const darkMode = colorScheme === 'dark';

  const getTextColor = () => {
    if (active) {
      if (textColor) return textColor;
      if (color) return color;
      return darkMode ? colors.blue[1] : 'black';
    }
    return darkMode ? 'white' : 'black';
  };

  const rightSlotWidth = secondaryAction || icon ? 90 : 30;

  return (
    <Box
      key={key}
      ref={ref}
      onClick={onClick}
      sx={(theme) => ({
        position: 'relative',
        backgroundColor: active && !darkMode ? bgColor : 'transparent',
        height: 32,
        display: 'flex',
        alignItems: 'center',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': {
          backgroundColor: darkMode ? theme.colors.dark[6] : theme.colors.gray[0],
        },
        '&:hover .booking-state-secondary-action': {
          opacity: 1,
        },
        ...sx,
      })}
    >
      <div
        style={{
          width: 5,
          height: 20,
          backgroundColor: active ? getTextColor() : 'transparent',
          borderRadius: 25,
          marginLeft: 25,
          marginRight: 20,
        }}
      />
      <Text
        className="overflow-ellipsis"
        color={getTextColor()}
        weight={active ? 'bolder' : 'normal'}
        sx={textStyle}
        style={{ maxWidth: width - rightSlotWidth }}
      >
        {label}
      </Text>
      {secondaryAction && (
        <Box
          className="booking-state-secondary-action"
          sx={(theme) => ({
            position: 'absolute',
            right: icon ? 36 : 8,
            opacity: 0.35,
            transition: 'opacity 120ms ease',
            display: 'flex',
            alignItems: 'center',
            '.mantine-ActionIcon-root': {
              color: darkMode ? theme.colors.gray[3] : theme.colors.gray[7],
            },
          })}
        >
          <Tooltip label={secondaryAction.label} withArrow position="left" openDelay={250}>
            <ActionIcon
              size="xs"
              variant="subtle"
              onClick={(event: MouseEvent<HTMLButtonElement>) => {
                event.stopPropagation();
                secondaryAction.onClick();
              }}
            >
              <IconFilter size={14} />
            </ActionIcon>
          </Tooltip>
        </Box>
      )}
      {icon && <Box sx={{ position: 'absolute', right: 10 }}>{icon}</Box>}
    </Box>
  );
};

export default BookingStateItem;
